/**
 * googleAnalytics.js — San Tomas Tourism College
 * ---------------------------------------------------------------
 * Google Analytics 4, behind the same consent gate as the Meta Pixel.
 *
 * This file exists because App.jsx currently contains:
 *
 *     ReactGA.initialize('G-XXXXXXXXXX');
 *
 * `G-XXXXXXXXXX` is Google's documentation placeholder, not a real
 * measurement ID. Analytics has therefore never recorded anything on
 * this site. On top of that the call sat at module scope, so it ran
 * before the app rendered and before any visitor could consent, and
 * nothing ever sent a pageview — so even with a real ID it would have
 * had the same SPA blind spot as the Meta snippet.
 *
 * Fixed here: the ID comes from the environment, initialisation waits
 * for consent, and pageviews follow react-router.
 *
 * If REACT_APP_GA4_ID is not set, this module does nothing at all —
 * which is still strictly better than shipping a placeholder.
 */

import { hasConsent, CONSENT_EVENT } from './consent';
import { isExcludedPath } from './metaPixel';

const GA_ID = process.env.REACT_APP_GA4_ID;
const ALLOW_IN_DEV = process.env.REACT_APP_PIXEL_ALLOW_DEV === 'true';

let ga = null;          // lazily imported react-ga4
let initialised = false;
let loading = false;
const pending = [];

const isBrowser = () => typeof window !== 'undefined';

const isLocalHost = () => {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.local');
};

/** A real GA4 measurement ID, not the placeholder from the docs. */
const idLooksReal = (id) => /^G-[A-Z0-9]{6,}$/i.test(id || '') && !/^G-X+$/i.test(id);

const canLoad = () => {
  if (!isBrowser()) return false;
  if (!idLooksReal(GA_ID)) return false;
  if (isLocalHost() && !ALLOW_IN_DEV) return false;
  if (window.navigator?.globalPrivacyControl === true) return false;
  if (isExcludedPath()) return false;
  return hasConsent();
};

/**
 * Load react-ga4 and initialise. The dynamic import keeps the library
 * out of the main bundle for visitors who decline.
 */
export const initGA = () => {
  if (initialised || loading) return;
  if (!canLoad()) return;

  loading = true;

  import('react-ga4')
    .then((mod) => {
      ga = mod.default;
      /* send_page_view: false is essential, not cosmetic.
         ReactGA.initialize() issues a gtag `config` command, and gtag
         sends a pageview automatically on config. Our own gaPageView()
         call below then sends a second one for the same page, so every
         session's landing page would be counted twice.
         Turning it off makes every pageview come from our explicit call:
         one per page, consistently, and still nothing on /admin.

         anonymizeIp is gone because GA4 anonymises IPs by default and
         ignores the flag — it was a Universal Analytics option. */
      ga.initialize(GA_ID, {
        gtagOptions: { send_page_view: false },
      });
      initialised = true;
      loading = false;

      gaPageView(window.location.pathname + window.location.search);
      while (pending.length) {
        const q = pending.shift();
        gaEvent(q.name, q.params);
      }
    })
    .catch(() => {
      /* react-ga4 missing or blocked. Analytics is not worth breaking
         the site over — fail silently and carry on. */
      loading = false;
    });
};

export const gaPageView = (path, title) => {
  if (!initialised || !ga) return;
  if (isExcludedPath()) return;
  ga.send({ hitType: 'pageview', page: path, title: title || document.title });
};

export const gaEvent = (name, params = {}) => {
  if (isExcludedPath()) return;
  if (!initialised || !ga) {
    pending.push({ name, params });
    initGA();
    return;
  }
  ga.event(name, params);
};

if (isBrowser()) {
  window.addEventListener(CONSENT_EVENT, (e) => {
    if (e.detail?.granted) initGA();
    else pending.length = 0;
  });
}

export const gaIsActive = () => initialised;
